import { platformCoordApi } from '@/components/cloudFunctions/platformCoord'
import type { CustomerId } from '@gh/common/models'

export type FollowupBookingContextPrefetch = {
  eligible: boolean
  preferredTellescopeUserId: string | null
  insurancePolicyLocked: boolean
}

const FOLLOWUP_PREFETCH_COALESCE_MS = 60_000
const COALESCE_WINDOW_MS = FOLLOWUP_PREFETCH_COALESCE_MS - 1_000

type CachedFollowupPrefetch = {
  fetchedAt: number
  result: FollowupBookingContextPrefetch
}

const inFlightByCustomer = new Map<CustomerId, Promise<FollowupBookingContextPrefetch>>()
const cacheByCustomer = new Map<CustomerId, CachedFollowupPrefetch>()

export function resetFollowupBookingContextPrefetch(customerId?: CustomerId): void {
  if (customerId) {
    inFlightByCustomer.delete(customerId)
    cacheByCustomer.delete(customerId)
    return
  }
  inFlightByCustomer.clear()
  cacheByCustomer.clear()
}

/** Read-only follow-up eligibility prefetch for booking session startup. */
export function prefetchFollowupBookingContextCoalesced(
  customerId: CustomerId,
): Promise<FollowupBookingContextPrefetch> {
  const now = Date.now()
  const inFlight = inFlightByCustomer.get(customerId)
  if (inFlight) {
    return inFlight
  }
  const cached = cacheByCustomer.get(customerId)
  if (cached && now - cached.fetchedAt < COALESCE_WINDOW_MS) {
    return Promise.resolve(cached.result)
  }
  const promise = (async (): Promise<FollowupBookingContextPrefetch> => {
    try {
      const response = await platformCoordApi.listCustomerConsultationDetails({
        limit: 1,
        followupBookingOnly: true,
      })
      const result: FollowupBookingContextPrefetch = {
        eligible: response.followupBooking?.eligible ?? false,
        preferredTellescopeUserId: response.followupBooking?.preferredTellescopeUserId ?? null,
        insurancePolicyLocked: response.followupBooking?.insurancePolicyLocked ?? false,
      }
      cacheByCustomer.set(customerId, { fetchedAt: Date.now(), result })
      return result
    } catch {
      const fallback: FollowupBookingContextPrefetch = {
        eligible: false,
        preferredTellescopeUserId: null,
        insurancePolicyLocked: false,
      }
      return fallback
    }
  })()
  inFlightByCustomer.set(customerId, promise)
  void promise.finally(() => {
    if (inFlightByCustomer.get(customerId) === promise) {
      inFlightByCustomer.delete(customerId)
    }
  })
  return promise
}
